import { openDB, IDBPDatabase, StoreKey, StoreValue } from 'idb';
import api from './api';

// Simplified DeviceData interface for use in functions
interface DeviceData {
  id?: number;
  deviceId: number;
  timestamp: string;
  data: Record<string, any>;
  synced: boolean;
}

// Simple database definition with store names only
interface MyDB {
  deviceData: {
    key: number;
    value: DeviceData;
    indexes: {
      'by-device': number;
      'by-synced': boolean;
    };
  };
  devices: {
    key: number;
    value: any;
  };
  pendingOperations: {
    key: number;
    value: {
      id?: number;
      url: string;
      method: string;
      data: any;
      timestamp: number;
    };
  };
}

let db: IDBPDatabase<MyDB> | null = null;

// Initialize the database
async function initDB() {
  if (!db) {
    db = await openDB<MyDB>('securat-offline', 1, {
      upgrade(database) {
        // Device data store
        const deviceDataStore = database.createObjectStore('deviceData', {
          keyPath: 'id',
          autoIncrement: true
        });
        deviceDataStore.createIndex('by-device', 'deviceId');
        deviceDataStore.createIndex('by-synced', 'synced');
        
        // Devices store
        database.createObjectStore('devices', {
          keyPath: 'id'
        });
        
        // Pending operations store
        database.createObjectStore('pendingOperations', {
          keyPath: 'id',
          autoIncrement: true
        });
      }
    });
  }
  return db;
}

// Cache devices for offline use
export async function cacheDevices(devices: any[]) {
  const database = await initDB();
  const tx = database.transaction('devices', 'readwrite');
  
  // Clear previous devices and add the new ones
  await tx.objectStore('devices').clear();
  for (const device of devices) {
    await tx.objectStore('devices').add(device);
  }
  
  await tx.done;
}

// Get devices from cache
export async function getOfflineDevices() {
  const database = await initDB();
  return database.getAll('devices');
}

// Save device data to IndexedDB for offline use
export async function saveDeviceData(deviceId: number, data: Record<string, any>) {
  const database = await initDB();
  
  const deviceData: DeviceData = {
    deviceId,
    timestamp: new Date().toISOString(),
    data,
    synced: navigator.onLine,
  };
  
  // If online, send to API immediately
  if (navigator.onLine) {
    try {
      await api.post(`/device-data/${deviceId}`, data);
    } catch (error) {
      // If API fails, still save locally but mark as not synced
      deviceData.synced = false;
    }
  }
  
  // Save to IndexedDB regardless of online status
  await database.add('deviceData', deviceData);
  
  return deviceData;
}

// Sync pending device data
export async function syncPendingData() {
  if (!navigator.onLine) return false;
  
  const database = await initDB();
  
  // Get all unsynced data (filter in memory since booleans are not valid IDB keys)
  const allData = await database.getAll('deviceData');
  const unsyncedData = allData.filter(d => !d.synced);
  
  for (const deviceData of unsyncedData) {
    try {
      // Try to sync with API
      await api.post(`/device-data/${deviceData.deviceId}`, deviceData.data);
      
      // Mark as synced
      deviceData.synced = true;
      await database.put('deviceData', deviceData);
    } catch (error) {
      console.error('Failed to sync device data:', error);
    }
  }
  
  // Now process any other pending operations
  const pendingOps = await database.getAll('pendingOperations');
  
  for (const operation of pendingOps) {
    try {
      // Execute the pending operation
      await api.request({
        url: operation.url,
        method: operation.method,
        data: operation.data,
      });
      
      // Remove the operation if successful
      await database.delete('pendingOperations', operation.id!);
    } catch (error) {
      console.error('Failed to execute pending operation:', error);
    }
  }
  
  return true;
}

// Queue an operation for when we're back online
export async function queueOperation(url: string, method: string, data: any) {
  const database = await initDB();
  
  await database.add('pendingOperations', {
    url,
    method,
    data,
    timestamp: Date.now(),
  });
}

// Setup sync event listeners
export function setupSyncListeners() {
  // Listen for online events
  window.addEventListener('online', () => {
    console.log('Back online, syncing data...');
    syncPendingData();
  });
  
  // Listen for custom sync event
  window.addEventListener('sync-data', () => {
    syncPendingData();
  });
}

// Initialize
export function initOfflineSupport() {
  initDB();
  setupSyncListeners();
}